import { apiClient } from '@/shared/lib/apiClient';
import type {
  ApiResponse,
  GuideApplicationInput,
  GuideApplicationResult,
  GuideLifecycleSnapshot,
  GuideProfile,
  ReferralCodeCheck,
} from '@/types';

export const guideProfileApi = {
  /** Public — no auth required. Creates the lightweight application. */
  async apply(input: GuideApplicationInput): Promise<GuideApplicationResult> {
    const response = await apiClient.post<ApiResponse<GuideApplicationResult>>(
      '/guide-profile/apply',
      input
    );
    return response.data.data!;
  },

  /** Public, rate-limited. Reveals only the referrer's first name. */
  async checkReferralCode(code: string): Promise<ReferralCodeCheck> {
    const response = await apiClient.get<ApiResponse<ReferralCodeCheck>>(
      `/growth/referral-codes/${encodeURIComponent(code.trim())}/validate`
    );
    return response.data.data!;
  },

  /** Lifecycle snapshot for the signed-in user. Safe for travelers too. */
  async getStatus(): Promise<GuideLifecycleSnapshot> {
    const response = await apiClient.get<ApiResponse<GuideLifecycleSnapshot>>(
      '/guide-profile/status'
    );
    return response.data.data!;
  },

  async getMyProfile(): Promise<{
    profile: GuideProfile;
    lifecycle: GuideLifecycleSnapshot;
  }> {
    const response = await apiClient.get<
      ApiResponse<{ profile: GuideProfile; lifecycle: GuideLifecycleSnapshot }>
    >('/guide-profile/me');
    return response.data.data!;
  },

  /** Partial write used by autosave. */
  async patchProfile(payload: Partial<GuideProfile>): Promise<GuideProfile> {
    const response = await apiClient.patch<ApiResponse<GuideProfile>>(
      '/guide-profile/profile',
      payload
    );
    return response.data.data!;
  },

  async submit(input: { agreementAccepted: boolean }): Promise<GuideProfile> {
    const response = await apiClient.post<ApiResponse<GuideProfile>>(
      '/guide-profile/submit',
      input
    );
    return response.data.data!;
  },
};

export const guideProfileKeys = {
  all: ['guide-profile'] as const,
  status: (userId?: string | null) =>
    [...guideProfileKeys.all, 'status', userId ?? 'anonymous'] as const,
  me: (userId?: string | null) =>
    [...guideProfileKeys.all, 'me', userId ?? 'anonymous'] as const,
};
